// Montagem do menu lateral (components/Sidebar.js), separada do React para ser
// testada (menuLateral.test.js).
//
// O menu é uma árvore: itens soltos na raiz e grupos com subitens. Um grupo pode
// ter seções (subtítulo discreto dentro do grupo, sem terceiro nível de abrir e
// fechar). As condições de visibilidade de cada item são as mesmas de antes da
// reorganização em grupos; só a ORGANIZAÇÃO é que muda por papel.
//
// Regras de poda (podarMenu):
//   - item invisível sai;
//   - seção sem item visível sai;
//   - grupo sem item visível sai;
//   - grupo com um item só não vira grupo: o item fica solto na raiz, na posição do grupo;
//   - grupo cujo único conteúdo é uma seção perde o subtítulo (seria redundante).
//
// O Perfil não está no menu lateral: fica no menu do avatar, no topo (components/MenuAvatar.js).

import { hasSystemModule } from './systemModules';
import { temAcessoTarefas } from '../pages/Tarefas/tarefasUtils';
import { temAcessoAndon } from '../utils/andon';
import {
  temAcessoAssinaturas, temAcessoFinanceiro, temAcessoLicencas, temAcessoPrestacao, temGestaoEscolar,
} from '../utils/roles';

export const ICONES = {
  home: '🏠',
  certificates: '🎓',
  ranking: '🏆',
  progress: '📈',
  adminPanel: '🛠️',
  schools: '🏫',
  coordinators: '🧭',
  exxer: '👥',
  temporary: '⏳',
  teachers: '👨‍🏫',
  students: '🎒',
  info: 'ℹ️',
  audit: '🧾',
  ai: '🤖',
  monitor: '👀',
  online: '🟢',
  reports: '📄',
  courses: '📚',
  link: '🔗',
  agenda: '🗓️',
  calendar: '📅',
  allocation: '📌',
  notices: '📢',
  register: '📝',
  createCourse: '✏️',
  myCourses: '📖',
  myExams: '📝',
  examResults: '✅',
  exams: '📝',
  linkExams: '🔗',
  gradeExams: '✅',
  attendance: '📋',
  chat: '💬',
  learning: '📘',
  tracking: '🎯',
  assistant: '🔎',
  tasks: '☑️',
  expenses: '🧾',
  finance: '💰',
  andon: '🚦',
  files: '📁',
  signatures: '✍️',
  cloud: '☁️',
  support: '🛟',
  softwares: '💿',
  licenses: '🔑',
  classes: '🏷️',
  people: '👥',
  // grupos novos
  schoolManagement: '🏛️',
  products: '📦',
  administration: '🛠️',
  classroom: '🏫',
};

const item = (id, rota, icone, rotulo, visivel = true, extra = {}) => ({
  tipo: 'item', id, rota, icone, rotulo, visivel: Boolean(visivel), ...extra,
});

// `ativoEm`: prefixos extras que também marcam o grupo como o da página atual
// (além das rotas dos próprios subitens).
const grupo = (id, icone, rotulo, filhos, extra = {}) => ({
  tipo: 'grupo', id, icone, rotulo, filhos, ...extra,
});

const secao = (id, rotulo, filhos) => ({ tipo: 'secao', id, rotulo, filhos });

// Traduz o usuário (papel ativo, flags e módulos) e o que vem da API (Cloud e
// Softwares) nos booleanos que a montagem usa.
export const contextoDoUsuario = (usuario, { cloudHabilitado = false, softwaresVisivel = false } = {}) => {
  const papel = usuario?.tipo_usuario || '';
  const adminLike = papel === 'admin' || papel === 'exxer';
  return {
    papel,
    gestaoEscolar: adminLike && temGestaoEscolar(usuario),
    modulos: {
      cursos: hasSystemModule(usuario, 'cursos'),
      cloud: hasSystemModule(usuario, 'cloud'),
      biblioteca: hasSystemModule(usuario, 'biblioteca'),
    },
    cloud: Boolean(cloudHabilitado),
    licencas: temAcessoLicencas(usuario),
    softwares: Boolean(softwaresVisivel),
    assinaturas: adminLike && temAcessoAssinaturas(usuario),
    tarefas: adminLike && temAcessoTarefas(usuario),
    // Prestação de Contas: o /me já traz o acesso calculado (fase de teste: só o admin).
    prestacao: adminLike && temAcessoPrestacao(usuario),
    financeiro: adminLike && temAcessoFinanceiro(usuario),
    andon: adminLike && temAcessoAndon(usuario),
  };
};

// Itens que aparecem do mesmo jeito em vários papéis.
const itemSuporte = () => item('suporte', '/suporte', ICONES.support, 'support');
const itemInicio = () => item('inicio', '/home', ICONES.home, 'home');
const itemAssistente = () => item('assistente', '/assistente', ICONES.assistant, 'assistant');
const itemCloud = (ctx) => item(
  'cloud', '/cloud', ICONES.cloud, 'cloudMqtt',
  ctx.papel !== 'temporario' && ctx.cloud && ctx.modulos.cloud
);
const itemLicencas = (ctx) => item('licencas', '/licencas', ICONES.licenses, 'licenses', ctx.licencas, { badge: 'licencas' });
const itemSoftwares = (ctx) => item('softwares', '/softwares', ICONES.softwares, 'softwares', ctx.softwares);
const itemBiblioteca = (visivel) => item('arquivos', '/biblioteca', ICONES.files, 'files', visivel);

const menuAdminExxer = (ctx) => {
  const admin = ctx.papel === 'admin';
  const gest = ctx.gestaoEscolar;
  return [
    itemInicio(),
    itemAssistente(),
    item('tarefas', '/tarefas', ICONES.tasks, 'tasks', ctx.tarefas),
    item('prestacao-contas', '/prestacao-contas', ICONES.expenses, 'expenseReports', ctx.prestacao),
    item('financeiro', '/financeiro/prestacao-contas', ICONES.finance, 'finance', ctx.financeiro),
    itemSuporte(),
    itemCloud(ctx),
    item('arquivos', '/admin/biblioteca', ICONES.files, 'files'),
    grupo('gestao-escolar', ICONES.schoolManagement, 'schoolManagement', [
      item('escolas', '/admin/escolas', ICONES.schools, 'schools', gest),
      item('coordenadores', '/admin/coordenadores', ICONES.coordinators, 'coordinators', gest),
      item('professores', '/coordenador/professores', ICONES.teachers, 'teachers', gest),
      item('alunos', '/coordenador/alunos', ICONES.students, 'students', gest),
      item('alunos-escolas', '/admin/alunos-escolas', ICONES.link, 'linkStudentSchools', gest),
      item('cadastro-lote', '/admin/cadastro-lote', ICONES.register, 'batchRegistration', gest),
      item('cursos', '/admin/cursos', ICONES.courses, 'courses', gest),
      // Telas do coordenador que o admin/Exxer usam em nome de um coordenador
      // escolhido (components/ExigeCoordenador.js).
      secao('coordenacao', 'coordination', [
        item('turmas', '/coordenador/turmas', ICONES.classes, 'classes', gest),
        item('vincular-alunos', '/coordenador/vincular-alunos', ICONES.people, 'linkStudents', gest),
        item('vincular-professores', '/coordenador/vincular-professores', ICONES.teachers, 'linkTeachers', gest),
      ]),
      secao('monitorar', 'monitor', [
        item('certificados', '/certificados', ICONES.certificates, 'certificates', gest),
        item('ranking', '/ranking', ICONES.ranking, 'ranking', gest),
        item('progresso', '/progresso', ICONES.progress, 'progress', gest),
      ]),
    ]),
    grupo('produtos', ICONES.products, 'products', [
      itemLicencas(ctx),
      itemSoftwares(ctx),
    ]),
    grupo('administracao', ICONES.administration, 'administration', [
      item('exxer', '/admin/exxer', ICONES.exxer, 'exxer', admin && gest),
      item('temporario', '/admin/temporario', ICONES.temporary, 'temporary', admin && gest),
      item('informacoes', '/admin/informacoes', ICONES.info, 'info', admin && gest),
      item('auditoria', '/admin/auditoria', ICONES.audit, 'audit', admin && gest),
      item('ajustes-ia', '/admin/ia', ICONES.ai, 'aiUsage', admin && gest),
      item('usuarios-online', '/monitorar/usuarios-online', ICONES.online, 'onlineUsers', admin && gest),
      item('assinaturas', '/assinaturas', ICONES.signatures, 'signatures', ctx.assinaturas),
      item('andon', '/andon', ICONES.andon, 'andon', ctx.andon),
    ]),
  ];
};

const menuCoordenador = (ctx) => {
  const cursos = ctx.modulos.cursos;
  const cadastro = cursos || ctx.modulos.cloud || ctx.modulos.biblioteca;
  return [
    itemInicio(),
    itemAssistente(),
    itemSuporte(),
    itemCloud(ctx),
    itemBiblioteca(ctx.modulos.biblioteca),
    grupo('cadastro', ICONES.register, 'register', [
      item('professores', '/coordenador/professores', ICONES.teachers, 'teachers', cadastro),
      item('turmas', '/coordenador/turmas', ICONES.classes, 'classes', cadastro && cursos),
      item('alunos', '/coordenador/alunos', ICONES.people, 'students', cadastro),
    ]),
    grupo('vinculos', ICONES.link, 'relation', [
      item('vincular-alunos', '/coordenador/vincular-alunos', ICONES.people, 'linkStudents', cursos),
      item('vincular-professores', '/coordenador/vincular-professores', ICONES.teachers, 'linkTeachers', cursos),
    ]),
    grupo('monitorar', ICONES.monitor, 'monitor', [
      item('certificados', '/certificados', ICONES.certificates, 'certificates', cursos),
      item('ranking', '/ranking', ICONES.ranking, 'ranking', cursos),
      item('progresso', '/progresso', ICONES.progress, 'progress', cursos),
      item('relatorios', '/relatorios', ICONES.reports, 'reports', cursos),
      item('cursos', '/cursos', ICONES.courses, 'courses', cursos),
    ]),
    grupo('agenda', ICONES.agenda, 'agenda', [
      item('calendario', '/coordenador/calendario', ICONES.calendar, 'calendar', cursos),
      item('alocacao', '/coordenador/alocacao', ICONES.allocation, 'allocation', cursos),
      item('avisos', '/coordenador/avisos', ICONES.notices, 'notices', cursos),
    ]),
    grupo('produtos', ICONES.products, 'products', [
      itemLicencas(ctx),
      itemSoftwares(ctx),
    ]),
  ];
};

const menuProfessor = (ctx) => {
  const cursos = ctx.modulos.cursos;
  return [
    itemInicio(),
    // Assistente IA só leitura: consultas e relatórios das turmas em que leciona.
    itemAssistente(),
    itemSuporte(),
    itemBiblioteca(ctx.modulos.biblioteca),
    itemCloud(ctx),
    grupo('cursos', ICONES.courses, 'courses', [
      item('criar-curso', '/professor/curso/novo', ICONES.createCourse, 'createCourse', cursos),
      item('meus-cursos', '/professor/cursos', ICONES.myCourses, 'myCourses', cursos),
    ], { ativoEm: ['/professor/curso'] }),
    grupo('provas', ICONES.exams, 'exams', [
      item('provas', '/professor/provas', ICONES.exams, 'exams', cursos),
      item('vincular-provas', '/professor/vincular-provas', ICONES.linkExams, 'linkExams', cursos),
      item('avaliar-provas', '/professor/avaliar-provas', ICONES.gradeExams, 'gradeExams', cursos),
    ]),
    grupo('monitorar', ICONES.monitor, 'monitor', [
      item('certificados', '/certificados', ICONES.certificates, 'certificates', cursos),
      item('ranking', '/ranking', ICONES.ranking, 'ranking', cursos),
      item('progresso', '/progresso', ICONES.progress, 'progress', cursos),
      item('relatorios', '/relatorios', ICONES.reports, 'reports', cursos),
    ]),
    grupo('sala-de-aula', ICONES.classroom, 'classroom', [
      item('presenca', '/professor/presenca', ICONES.attendance, 'attendanceList', cursos),
      item('calendario', '/professor/calendario', ICONES.calendar, 'calendar', cursos),
      item('chat', '/chat', ICONES.chat, 'chat', cursos),
    ]),
    itemLicencas(ctx),
    itemSoftwares(ctx),
  ];
};

const menuAluno = (ctx) => {
  const cursos = ctx.modulos.cursos;
  return [
    itemInicio(),
    item('calendario', '/aluno/calendario', ICONES.calendar, 'calendar', cursos),
    item('chat', '/chat', ICONES.chat, 'chat', cursos),
    itemSuporte(),
    itemBiblioteca(ctx.modulos.biblioteca),
    itemCloud(ctx),
    grupo('aprendizado', ICONES.learning, 'learning', [
      item('meus-cursos', '/aluno/meus-cursos', ICONES.myCourses, 'myCourses', cursos),
      item('provas', '/aluno/provas', ICONES.myExams, 'myExams', cursos),
      item('resultado-provas', '/aluno/resultado-provas', ICONES.examResults, 'examResults', cursos),
    ]),
    grupo('acompanhamento', ICONES.tracking, 'tracking', [
      item('progresso', '/progresso', ICONES.progress, 'progress', cursos),
      item('ranking', '/ranking', ICONES.ranking, 'ranking', cursos),
      item('certificados', '/certificados', ICONES.certificates, 'certificates', cursos),
    ]),
    itemLicencas(ctx),
    itemSoftwares(ctx),
  ];
};

// Temporário: sem mudança (a ordem é a de antes da reorganização).
const menuTemporario = (ctx) => [
  itemInicio(),
  itemLicencas(ctx),
  itemSoftwares(ctx),
  itemSuporte(),
  itemBiblioteca(true),
];

// Papel fora dos conhecidos: mantido como antes da reorganização.
const menuOutros = (ctx) => [
  itemInicio(),
  item('certificados', '/certificados', ICONES.certificates, 'certificates'),
  item('ranking', '/ranking', ICONES.ranking, 'ranking'),
  item('progresso', '/progresso', ICONES.progress, 'progress'),
  itemCloud(ctx),
  itemLicencas(ctx),
  itemSoftwares(ctx),
  itemSuporte(),
];

const contarItens = (filhos) => filhos.reduce(
  (total, filho) => total + (filho.tipo === 'secao' ? filho.filhos.length : 1),
  0
);

const limparItem = ({ visivel, ...resto }) => resto;

export const podarMenu = (nos) => nos.flatMap((no) => {
  if (no.tipo === 'item') return no.visivel ? [limparItem(no)] : [];

  let filhos = no.filhos.flatMap((filho) => {
    if (filho.tipo === 'secao') {
      const itens = filho.filhos.filter((f) => f.visivel).map(limparItem);
      return itens.length ? [{ ...filho, filhos: itens }] : [];
    }
    return filho.visivel ? [limparItem(filho)] : [];
  });

  const total = contarItens(filhos);
  if (total === 0) return [];
  if (total === 1) {
    const unico = filhos[0].tipo === 'secao' ? filhos[0].filhos[0] : filhos[0];
    return [unico];
  }
  if (filhos.length === 1 && filhos[0].tipo === 'secao') filhos = filhos[0].filhos;

  return [{ ...no, filhos }];
});

const MONTADORES = {
  admin: menuAdminExxer,
  exxer: menuAdminExxer,
  coordenador: menuCoordenador,
  professor: menuProfessor,
  aluno: menuAluno,
  temporario: menuTemporario,
};

// Devolve a árvore já podada para o contexto (contextoDoUsuario).
export const montarMenuLateral = (ctx) => {
  const montador = MONTADORES[ctx?.papel] || menuOutros;
  return podarMenu(montador(ctx));
};

// Itens (folhas) de um grupo, atravessando as seções.
export const itensDoGrupo = (grupoNo) => grupoNo.filhos.flatMap(
  (filho) => (filho.tipo === 'secao' ? filho.filhos : [filho])
);

// Todas as rotas que o menu mostra (raiz e subitens).
export const rotasDoMenu = (arvore) => arvore.flatMap(
  (no) => (no.tipo === 'grupo' ? itensDoGrupo(no).map((i) => i.rota) : [no.rota])
);

// O grupo da página atual: o primeiro cujo subitem (ou prefixo extra) casa com o caminho.
export const grupoDaRota = (arvore, caminho = '') => {
  const casa = (prefixo) => prefixo && caminho.startsWith(prefixo);
  const achado = arvore.find((no) => no.tipo === 'grupo' && (
    itensDoGrupo(no).some((i) => casa(i.rota)) || (no.ativoEm || []).some(casa)
  ));
  return achado ? achado.id : null;
};

// ── Acordeão do menu expandido ────────────────────────────────────────────────
// Só um grupo aberto por vez (ou nenhum): o estado é o id do grupo aberto ou null.
// - navegar: abre o grupo da página nova e fecha os demais; página solta da raiz
//   fecha todos. Um grupo fechado à mão reabre na próxima navegação para ele;
// - alternar: clicar no cabeçalho abre aquele grupo e fecha os outros; clicar no
//   cabeçalho já aberto o fecha.
export const acordeaoMenu = (aberto, acao) => {
  switch (acao?.tipo) {
    case 'navegar':
      return grupoDaRota(acao.arvore || [], acao.caminho);
    case 'alternar':
      return aberto === acao.id ? null : acao.id;
    default:
      return aberto;
  }
};

// Chaves da antiga memória de grupos abertos (`sidebar-grupos:<papel>`), que o
// acordeão aposentou. Remove todas; sem armazenamento (aba privada, bloqueio), ignora.
export const PREFIXO_GRUPOS_ANTIGO = 'sidebar-grupos:';
export const limparMemoriaGruposAntiga = (armazenamento) => {
  try {
    const store = armazenamento || window.localStorage;
    const chaves = [];
    for (let i = 0; i < store.length; i += 1) {
      const chave = store.key(i);
      if (chave && chave.startsWith(PREFIXO_GRUPOS_ANTIGO)) chaves.push(chave);
    }
    chaves.forEach((chave) => store.removeItem(chave));
  } catch (_err) {
    // sem armazenamento: nada a limpar
  }
};
